import React, { useState, useEffect } from "react";
import "./DonorsList.css";

/* ================= SVG ICONS ================= */
const MessageIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 10h10" />
    <path d="M7 14h6" />
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

/* ================= COMPONENT ================= */
function DonorsList() {
  const [donors, setDonors] = useState([]);

  // Load records from persistent LocalStorage upon load
  useEffect(() => {
    const rawData = localStorage.getItem("ANUK_DONORS_LIST");
    if (rawData) {
      setDonors(JSON.parse(rawData));
    } else {
      // Setup some default demo data if pure new instance
      const defaultRecords = [
        { id: "DON-1001", name: "Suresh Kumar", mobile: "9876543210", amount: "5000", location: "Chennai", occasion: "Birthday Celebration", date: "15 Jan 2024" },
        { id: "DON-1002", name: "Lakshmi Iyer", mobile: "9123456780", amount: "10000", location: "Puducherry", occasion: "Marriage Anniversary", date: "16 Jan 2024" }
      ];
      setDonors(defaultRecords);
      localStorage.setItem("ANUK_DONORS_LIST", JSON.stringify(defaultRecords));
    }
  }, []);

  // Action: Remove Record
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to drop this donor record?")) {
      const filtered = donors.filter((item) => item.id !== id);
      setDonors(filtered);
      localStorage.setItem("ANUK_DONORS_LIST", JSON.stringify(filtered));
    }
  };

  // Action: Open WhatsApp Welcome Notice
  const handleWelcomeMsg = (record) => {
    const cleanMobile = record.mobile.replace(/\D/g, "");
    const destinationNumber = cleanMobile.length === 10 ? "91" + cleanMobile : cleanMobile;
    
    const textMsg = `Namaste ${record.name},\n\nWelcome to the *Anuk Charitable Trust* family! 🙏\nWe have registered your details in our system. Thank you for joining our mission to empower lives, ensure dignity, and provide continuous community care.\n\nBest Regards,\n*Anuk Charitable Trust Administration*`;
    
    const whatsappLink = `https://wa.me/${destinationNumber}?text=${encodeURIComponent(textMsg)}`;
    window.open(whatsappLink, "_blank");
  };

  // Action: Open WhatsApp Thank You Gratitude
  const handleThankYouMsg = (record) => {
    const cleanMobile = record.mobile.replace(/\D/g, "");
    const destinationNumber = cleanMobile.length === 10 ? "91" + cleanMobile : cleanMobile;
    
    const textMsg = `Dear ${record.name},\n\nWe extend our absolute gratitude for your generous donation of *₹${record.amount}* made towards '${record.occasion}'. ✨\n\nYour support bridges relief directly to underprivileged students and elderly individuals. May good fortune bless you continuously.\n\nWarm Regards,\n*Anuk Charitable Trust*`;
    
    const whatsappLink = `https://wa.me/${destinationNumber}?text=${encodeURIComponent(textMsg)}`;
    window.open(whatsappLink, "_blank");
  };

  return (
    <div className="admin-page-wrapper">
      
      {/* ================= HERO BANNER ================= */}
      <section className="admin-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Registry Database</h1>
          <div className="hero-breadcrumb">
            <span className="breadcrumb-link">Admin Portal</span>
            <span className="breadcrumb-arrow">➔</span>
            <span className="breadcrumb-current">Donors List</span>
          </div>
        </div>
      </section>

      {/* ================= TABLE DASHBOARD ================= */}
      <section className="admin-table-section">
        <div className="admin-container">
          
          <div className="dashboard-header">
            <div>
              <span className="badge-admin">OPERATIONS DIRECTORY</span>
              <h2 className="dashboard-title">Official Donor Submissions</h2>
              <p className="dashboard-desc">
                Review verified incoming records and dispatch synchronized notifications directly via WhatsApp integrations.
              </p>
            </div>
            <div className="total-metrics">
              <span className="metric-count">{donors.length}</span>
              <span className="metric-label">Registered Submissions</span>
            </div>
          </div>

          <div className="table-card">
            
            <div className="table-responsive-wrapper">
              <table className="donors-data-table">
                
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Donor Name</th>
                    <th>Mobile</th>
                    <th>Amount (₹)</th>
                    <th>Location</th>
                    <th>Occasion / Offering</th>
                    <th className="action-col">Integrated Row Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {donors.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-row">
                        No submissions logged yet. Submitting the donation form will instantly populate records here.
                      </td>
                    </tr>
                  ) : (
                    donors.map((item) => (
                      <tr key={item.id}>
                        <td className="cell-date">{item.date}</td>
                        <td className="cell-name bold">{item.name}</td>
                        <td className="cell-mobile">{item.mobile}</td>
                        <td className="cell-amount bold">₹{item.amount}</td>
                        <td className="cell-loc">{item.location}</td>
                        <td className="cell-occ">
                          <span className="occ-pill">{item.occasion}</span>
                        </td>
                        <td className="cell-actions">
                          <div className="action-button-group">
                            
                            {/* Send Welcome Notification */}
                            <button 
                              onClick={() => handleWelcomeMsg(item)} 
                              className="btn-action welcome"
                              title="Send Welcome Notification via WhatsApp"
                            >
                              <MessageIcon />
                              <span>Welcome</span>
                            </button>

                            {/* Send Gratitude Acknowledgment */}
                            <button 
                              onClick={() => handleThankYouMsg(item)} 
                              className="btn-action thanks"
                              title="Send Gratitude Acknowledgment via WhatsApp"
                            >
                              <MessageIcon />
                              <span>Thank You</span>
                            </button>

                            {/* Delete specific entry */}
                            <button 
                              onClick={() => handleDelete(item.id)} 
                              className="btn-action delete"
                              title="Delete Record"
                            >
                              <TrashIcon />
                            </button>

                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

              </table>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
}

export default DonorsList;